// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useDialogControl } from '@hooks/other/useDialogControl';
import useUserService from '@hooks/queries/credential/onboarding/useUserService';
import { useState } from 'react';
import { MoreVert } from '@mui/icons-material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { Credential } from '@daml.js/utility-credential-v0/lib/Utility/Credential/V0/Credential';
import { ChoiceDialog } from '../../components/ChoiceDialog';
import Loading from '../../components/Loading';
import PartyDisplay from '../../components/PartyDisplay';
import { DataTable } from '../../components/Table/DataTable';
import { useModuleParty } from '../../contexts/ModuleContext';
import useCredentials from '../../hooks/queries/credential/useCredentials';
import Claims from '../../modules/Credential/Claims';
import TemplateContract from '../../utils/TemplateContract';
import { extractPrefix } from '../../utils/common';
import { PageWrapper } from '../PageWrapper';
import { CredentialDetails } from './CredentialDetails';
import { OfferCredential } from './OfferCredential';
import { useCreateCredentialActions } from './useCreateCredentialsActions';

type Row = TemplateContract<Credential>;
interface MoreDotsProps {
  issuer: string;
  holder: string;
}
const ActionsIndicator: React.FC<MoreDotsProps> = ({ issuer, holder }) => {
  const party = useModuleParty();
  const isIssuer = party === issuer;
  const isHolder = party === holder;
  if (isIssuer || isHolder) {
    return <MoreVert fontSize="small" />;
  }
  return null;
};
const createHeaders: () => GridColDef<Row>[] = () => {
  return [
    {
      field: 'issuer',
      headerName: 'Issuer',
      renderCell: (params) => <PartyDisplay party={params.row.payload.issuer} />,
      valueGetter: (params) => extractPrefix(params.row.payload.issuer, '::'),
      flex: 1,
      filterable: true,
    },
    {
      field: 'holder',
      headerName: 'Holder',
      renderCell: (params) => <PartyDisplay party={params.row.payload.holder} />,
      valueGetter: (params) => extractPrefix(params.row.payload.holder, '::'),

      flex: 1,
      filterable: true,
    },
    {
      field: 'id',
      headerName: 'Id',
      valueGetter: (params) => params.row.payload.id,
      flex: 1,
      filterable: true,
    },
    {
      field: 'claims',
      headerName: 'Claims',
      renderCell: (params) => <Claims claims={params.row.payload.claims} />,
      flex: 3,
      sortable: false,
      filterable: false,
    },
    {
      maxWidth: 5,
      field: 'action',
      hideable: true,
      headerName: '',
      align: 'right',
      renderCell: (params) => (
        <ActionsIndicator holder={params.row.payload.holder} issuer={params.row.payload.issuer} />
      ),
    },
  ];
};

interface CredentialsProps {
  disableVirtualization?: boolean;
}
export const Credentials: React.FC<CredentialsProps> = ({ disableVirtualization }) => {
  const userService = useUserService();
  const credentials = useCredentials();
  const [selectedRow, setSelectedRow] = useState<TemplateContract<Credential> | undefined>();
  const onRowClick = (r: GridRowParams<TemplateContract<Credential>>) => {
    setSelectedRow(r.row);
  };
  const { setDialogName, dialogName, onCloseDialog } = useDialogControl();
  const onClose = () => {
    setSelectedRow(undefined);
  };
  const actions = useCreateCredentialActions(onClose, selectedRow);
  const isLoading = credentials.isLoading || userService.isLoading;
  if (isLoading) return <Loading />;
  const buttons = [
    {
      name: 'Offer Credential',
      onClick: () => setDialogName('offer'),
    },
  ];
  return (
    <PageWrapper buttons={buttons} pageName="Credentials">
      <ChoiceDialog
        actions={actions}
        title="Credential Details"
        open={!!selectedRow}
        onClose={onClose}
      >
        {selectedRow && <CredentialDetails row={selectedRow} />}
      </ChoiceDialog>
      {dialogName === 'offer' && <OfferCredential open onClose={onCloseDialog} />}

      <DataTable
        columns={createHeaders()}
        rows={credentials?.data || []}
        rowKey={(a) => a.contractId}
        onRowClick={onRowClick}
        disableVirtualization={disableVirtualization}
      />
    </PageWrapper>
  );
};
